import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { ShareMode } from '@shared/config/navigation.config';
import { Contact } from '@shared/models';
import {
  ContactType,
  GROUP_DEFAULT_PHOTO,
  TeamChannelContact,
  USER_DEFAULT_PHOTO,
} from '@shared/models/collaboration.model';
import {
  GraphApiChat,
  GraphApiChatResponse,
  GraphApiContact,
  GraphApiContactResponse,
  GraphApiMailBody,
  GraphApiMessageRequest,
  GraphApiTeam,
  GraphApiTeamChannel,
  GraphApiTeamChannelResponse,
  GraphApiTeamResponse,
} from '@shared/models/graph-api.model';
import { Observable, combineLatest, forkJoin, of } from 'rxjs';
import {
  catchError,
  defaultIfEmpty,
  map,
  shareReplay,
  switchMap,
} from 'rxjs/operators';
import { HttpErrorSkip } from 'src/app/interceptors/http-error/http-error-skip.model';

@Injectable({
  providedIn: 'root',
})
export class GraphApiHttpService {
  private readonly BASE_URL = 'https://graph.microsoft.com';
  private readonly MAX_NAMES_IN_GROUP_DISPLAY = 4;

  constructor(
    private http: HttpClient,
    private sanitizer: DomSanitizer
  ) {}

  getProfile(): Observable<GraphApiContact> {
    return this.http.get<GraphApiContact>(`${this.BASE_URL}/v1.0/me`);
  }

  public getUsers(
    shareMode: ShareMode,
    name: string,
    top: number,
    myId: string
  ): Observable<Contact[]> {
    const url = name
      ? // eslint-disable-next-line @stylistic/max-len
        `${this.BASE_URL}/v1.0/users?$filter=startswith(displayName,'${name}') or startswith(givenName,'${name}') or startswith(surname,'${name}') or startswith(mail,'${name}') or startswith(userPrincipalName,'${name}')&$select=id,displayName,mail&$top=${top}`
      : `${this.BASE_URL}/v1.0/me/people?$select=id,displayName,userPrincipalName`;

    return this.http.get<GraphApiContactResponse>(url).pipe(
      map(({ value }) =>
        shareMode === ShareMode.Email
          ? value
          : value.filter(contact => contact.id !== myId)
      ),
      switchMap((graphApiContacts: GraphApiContact[]) => {
        return forkJoin(
          graphApiContacts.map(graphApiContact =>
            this.getProfilePhoto(graphApiContact.id).pipe(
              map((photo: SafeUrl) => ({
                ...graphApiContact,
                id:
                  shareMode === ShareMode.Email
                    ? graphApiContact.mail || graphApiContact.userPrincipalName
                    : graphApiContact.id,
                mail: graphApiContact.mail || graphApiContact.userPrincipalName,
                displayName: graphApiContact.displayName || '',
                photo: photo || USER_DEFAULT_PHOTO,
                type:
                  shareMode === ShareMode.Email
                    ? ContactType.EMAIL
                    : ContactType.ONE_ON_ONE,
              }))
            )
          )
        ).pipe(defaultIfEmpty([]));
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  public getGroups(name: string): Observable<Contact[]> {
    const url = name
      ? // eslint-disable-next-line @stylistic/max-len
        `${this.BASE_URL}/v1.0/groups/?$filter=startswith(displayName,'${name}') or startswith(mail,'${name}')&$select=id,displayName,mail,Visibility`
      : `${this.BASE_URL}/v1.0/me/memberof?&$select=id,displayName,mail,visibility`;

    return this.http
      .get<GraphApiContactResponse>(url, {
        headers: {
          'Content-Type': 'application/json',
        },
      })
      .pipe(
        map<GraphApiContactResponse, GraphApiContact[]>(graphApiResponse =>
          graphApiResponse.value.filter(
            graphApiContact => graphApiContact.visibility !== 'Private'
          )
        ),
        map<GraphApiContact[], Contact[]>(graphApiContacts =>
          graphApiContacts.map(contact => ({
            ...contact,
            displayName: contact.displayName || contact.id,
            photo: GROUP_DEFAULT_PHOTO,
            type: ContactType.GROUP,
          }))
        ),
        shareReplay({ bufferSize: 1, refCount: true })
      );
  }

  public getGroupsAndMeetingsByName(
    name: string,
    top: number,
    myDisplayName: string
  ): Observable<Contact[]> {
    const url =
      `${this.BASE_URL}/v1.0/me/chats?$select=id,topic,chatType&$expand=members&` +
      `$filter=chatType ne 'oneOnOne' and (contains(tolower(topic), tolower('${name}')) ` +
      `or members/any(s:startsWith(tolower(s/displayName), tolower('${name}'))))&$top=${top}`;

    return this.http.get<GraphApiChatResponse>(url).pipe(
      map<GraphApiChatResponse, GraphApiChat[]>(
        graphApiResponse => graphApiResponse.value
      ),
      map(graphApiChats =>
        graphApiChats.map(graphApiChat => {
          graphApiChat.members = graphApiChat.members.filter(
            member => member.displayName !== myDisplayName
          );
          return graphApiChat;
        })
      ),
      map<GraphApiChat[], Contact[]>(graphApiChats =>
        graphApiChats
          .map<Contact>(graphApiChat => ({
            ...graphApiChat,
            displayName:
              graphApiChat.topic ||
              this.shortenGroupName(
                graphApiChat.members
                  .map(member => member.displayName)
                  .join(', ')
              ),
            photo: GROUP_DEFAULT_PHOTO,
            type: <ContactType>graphApiChat.chatType,
          }))
          .sort((a, b) => a.type.localeCompare(b.type))
      ),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  private shortenGroupName(members: string): string {
    const arr = members.split(', ');
    if (arr.length > this.MAX_NAMES_IN_GROUP_DISPLAY) {
      const membersList = arr
        .slice(0, this.MAX_NAMES_IN_GROUP_DISPLAY)
        .join(', ');
      const excessCount = arr.length - this.MAX_NAMES_IN_GROUP_DISPLAY;
      members = `${membersList} and ${excessCount} more`;
    }
    return members;
  }

  public getTeamsChannels(name: string) {
    const url = `${this.BASE_URL}/v1.0/me/joinedTeams?$select=id,displayName`;
    return this.http.get<GraphApiTeamResponse>(url).pipe(
      map(team =>
        team.value.filter(team =>
          team.displayName.toLowerCase().includes(name.toLowerCase())
        )
      ),
      switchMap(teams =>
        combineLatest(teams.map(team => this.getChannels(team)))
      ),
      defaultIfEmpty([]),
      map(channels => channels.flat()),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  private getChannels(team: GraphApiTeam): Observable<TeamChannelContact[]> {
    const url = `${this.BASE_URL}/v1.0/teams/${team.id}/channels?$select=id,displayName`;

    return this.http.get<GraphApiTeamChannelResponse>(url).pipe(
      map(response => response.value),
      defaultIfEmpty([]),
      map<GraphApiTeamChannel[], TeamChannelContact[]>(channels => {
        return channels.map(channel => ({
          id: channel.id,
          teamId: team.id,
          displayName: `${team.displayName} - ${channel.displayName}`,
          type: ContactType.TEAM_CHANNEL,
          photo: GROUP_DEFAULT_PHOTO,
        }));
      }),
      catchError(() => {
        return of(null);
      })
    );
  }

  public getMyProfilePhoto(): Observable<SafeUrl> {
    const url = `${this.BASE_URL}/v1.0/me/photo/$value`;
    return this.getAnyProfilePhoto(url);
  }

  public getProfilePhoto(userId: string): Observable<SafeUrl> {
    const url = `${this.BASE_URL}/v1.0/users/${userId}/photo/$value`;
    return this.getAnyProfilePhoto(url);
  }

  private getAnyProfilePhoto(url: string) {
    return this.http
      .get(url, {
        responseType: 'blob',
        headers: {
          [HttpErrorSkip.HEADER_KEY]: new HttpErrorSkip({
            skipStatusCodes: [400, 401, 403, 404],
          }).parseToString(),
        },
      })
      .pipe(
        map((data: Blob) => {
          const objectURL = URL.createObjectURL(data);
          return this.sanitizer.bypassSecurityTrustUrl(objectURL);
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
        catchError(() => {
          return of(null);
        })
      );
  }

  public createOneToOneChat(contact: Contact, myID: string) {
    const url = `${this.BASE_URL}/v1.0/chats`;
    const body = {
      chatType: 'oneOnOne',
      members: [
        {
          '@odata.type': '#microsoft.graph.aadUserConversationMember',
          roles: ['owner'],
          'user@odata.bind': `${this.BASE_URL}/v1.0/users(\'${myID}\')`,
        },
        {
          '@odata.type': '#microsoft.graph.aadUserConversationMember',
          roles: ['owner'],
          'user@odata.bind': `${this.BASE_URL}/v1.0/users(\'${contact.id}\')`,
        },
      ],
    };
    return this.http.post(url, body);
  }

  public sendChatMessage(
    chatId: string,
    messageRequest: GraphApiMessageRequest
  ) {
    return this.sendMessage(
      `${this.BASE_URL}/v1.0/chats/${chatId}/messages`,
      messageRequest
    );
  }

  public sendTeamChannelMessage(
    contact: TeamChannelContact,
    messageRequest: GraphApiMessageRequest
  ) {
    return this.sendMessage(
      `${this.BASE_URL}/v1.0/teams/${contact.teamId}/channels/${contact.id}/messages`,
      messageRequest
    );
  }

  private sendMessage(url: string, messageRequest: GraphApiMessageRequest) {
    return this.http.post(url, this.generateSendMessageRequest(messageRequest));
  }

  public sendEmail(emailBody: GraphApiMailBody) {
    return this.http.post<any>(`${this.BASE_URL}/v1.0/me/sendMail`, emailBody);
  }

  private generateSendMessageRequest(messageRequest: GraphApiMessageRequest) {
    return {
      body: {
        contentType: 'html',
        content: messageRequest.body,
      },
      ...(messageRequest.base64Image
        ? {
            hostedContents: [
              {
                '@microsoft.graph.temporaryId': '1',
                contentBytes: messageRequest.base64Image.split(',')[1],
                contentType: 'image/png',
              },
            ],
          }
        : {}),
    };
  }
}
